import CancelableApi from "./CancelableApi";
import CancelableAxiosInstance from "./CancelableAxiosInstance";
import { RegionGroup, Series } from "./StudyApi";
import { AxiosPromise } from "axios";

export interface Eval {
  code: string;
}

export type EvalStudyParamType = {
  code: string;
  required: boolean;
};

export type ParamsByObsType = { [k: string]: EvalStudyParamType[] };

export interface EvalStudy {
  id: string;
  series: Array<Series>;
  passes: Array<EvalPass>;
  obsTypes: string[];
  patientSex?: string;
  patientAge?: number;
  studyId: string;
  aiResult: AiResult;
  aiMode: EvalAiMode;
  tags?: string[];
  rdParamsByObsType?: ParamsByObsType;
  gtParamsByObsType?: ParamsByObsType;
  displayResultAtEndOfEachPass: boolean;
  labelableImageTags: string[];
  gtWithReports: boolean;
}

export enum AiResult {
  NO_FRACT = "NO_FRACT",
  DOUBT_FRACT = "DOUBT_FRACT",
  FRACT = "FRACT",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}

export interface BaseEvalPass {
  workflow: EvalWorkflow;
  observations: Array<EvalObs>;
  markers: Array<Marker>;
  tags: Array<string>;
  _passColor?: string;
  _passIdx?: number;
}

export interface EvalPass extends BaseEvalPass {
  markersBeforeAiActivation?: Array<Marker>;
  tags: Array<string>;
  secDurationBeforeAiActivation?: number;
  secDuration?: number;
}

export interface PassResults {
  readerWithoutAiStat: PassResult;
  readerWithAiStat: PassResult;
  gleamerAiStat: PassResult;
}

export interface PassResult {
  globalStat: PassStat;
  statByObsType: Map<string, PassStat>;
}

export interface PassStat {
  falsePositive: number;
  falseNegative: number;
  truePositive: number;
  trueNegative: number;
}

export interface WithObsTypeAndParams {
  obsType: string;
  valueByParam?: Record<string, string>;
}

export interface EvalObs extends RegionGroup, WithObsTypeAndParams {
  obsType: string;
  boneCode: string;
  valueByParam?: { [k: string]: string };
}

export type HoveredFromState = "aside" | "viewport" | null;

export interface Marker extends WithObsTypeAndParams {
  imageId: string;
  x: number;
  y: number;
  _label: string;
  _hoveredFrom: HoveredFromState;
}

export enum EvalWorkflow {
  GT = "GT",
  GT_REC = "GT_REC",
  RD = "RD",
  RD_AI = "RD_AI",
}

export enum EvalAiMode {
  VIEWER = "VIEWER",
  GMSK = "GMSK",
}

export default class EvalWorkflowApi extends CancelableApi {
  static with(axios: CancelableAxiosInstance) {
    return new EvalWorkflowApi(axios);
  }

  findNextStudy(workflow: EvalWorkflow, evalCode: string): AxiosPromise<EvalStudy> {
    return this.client.get(`/eval-workflows/${workflow}/evals/${evalCode}/studies/next`, {
      validateStatus: (status) => status === 200 || status === 403,
    });
  }

  async savePass(evalCode: string, evalStudyId: string, pass: EvalPass): Promise<EvalStudy> {
    const resp = await this.client.post(
      `/eval-workflows/${pass.workflow}/evals/${evalCode}/studies/${evalStudyId}/passes`,
      pass
    );
    return resp.data;
  }

  async findEval(workflow: EvalWorkflow): Promise<Eval> {
    const resp = await this.client.get(`/eval-workflows/${workflow}`);
    return resp.data;
  }

  async computeResult(evalCode: string, evalStudyId: string, pass: EvalPass): Promise<PassResults> {
    const resp = await this.client.post(
      `/eval-workflows/${pass.workflow}/evals/${evalCode}/studies/${evalStudyId}/pass-results`,
      pass
    );
    return resp.data;
  }
}
